import { useState } from "react";
import { ArrowDownToLine, ArrowUpToLine, PanelLeft, PanelRight, Plus } from "lucide-react";
import { TOPS, addSidelites, addTransom, layout, patchAt, pieceNames, removeUnit, setPieceSize, stackPiece } from "@/lib/design";

const inp = "h-11 w-full rounded-[10px] border border-black/10 bg-white px-3 text-[15px] text-black outline-none focus:border-[#007AFF]";
const btn = "inline-flex h-11 items-center justify-center gap-1.5 rounded-full border border-black/10 bg-white px-4 text-[14px] font-medium text-black transition-colors hover:border-[#007AFF] disabled:opacity-40";
const Lab = ({ t, children }) => <label className="block text-[12px] font-semibold uppercase tracking-wide text-[#6D6D72]">{t}<div className="mt-1 normal-case tracking-normal">{children}</div></label>;

const unitOf = (L, path) => [...L.units].reverse().find((u) => path.join().startsWith(u.path.join()) && path.length > u.path.length) || L.units[0];

function SizeInput({ value, testId, onCommit }) {
  return <input key={`${testId}-${value}`} data-testid={testId} inputMode="decimal" defaultValue={Math.round(value * 8) / 8} className={inp}
    onBlur={(e) => { const v = Number(e.target.value); if (v !== value && onCommit(v) === false) e.target.value = Math.round(value * 8) / 8; }}
    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />;
}

// Selected piece: exact width/height (totals follow), top shape, remove.
export function SelectedPiece({ d, path, set, onMsg }) {
  const L = layout(d);
  const u = unitOf(L, path);
  const name = pieceNames(d)[L.units.indexOf(u)];
  const commit = (dim) => (v) => {
    if (!(v >= 12)) { onMsg("Minimum piece size 12 in"); return false; }
    set(setPieceSize(d, u.path, dim, v));
  };
  return (
    <div className="space-y-3 rounded-[14px] bg-white p-4" data-testid="design-piece-panel">
      <p className="text-[17px] font-semibold text-black" data-testid="design-piece-name">{name}</p>
      <div className="grid grid-cols-2 gap-2">
        <Lab t="Width (in)"><SizeInput value={u.w} testId="piece-width-input" onCommit={commit("w")} /></Lab>
        <Lab t="Height (in)"><SizeInput value={u.h} testId="piece-height-input" onCommit={commit("h")} /></Lab>
        <Lab t="Top shape">
          <select data-testid="piece-top-select" value={u.node.top || "rect"} onChange={(e) => set(patchAt(d, u.path, { top: e.target.value }))} className={inp}>
            {TOPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Lab>
        {L.units.length > 1 && <div className="flex items-end"><button data-testid="remove-piece-button" onClick={() => set(removeUnit(d, u.path))} className={`${btn} w-full text-[#D70015]`}>Remove piece</button></div>}
      </div>
    </div>
  );
}

// Add-on pieces: sidelites (left / right / both), transom (rectangle / arch / half-round), stacked units.
export function AddPieces({ d, set }) {
  const [sw, setSw] = useState(14);
  const [th, setTh] = useState(18);
  const [top, setTop] = useState("rect");
  const [st, setSt] = useState({ side: "top", size: 24, kind: "window" });
  const n = (v) => Math.max(12, Number(v) || 12);
  return (
    <div className="space-y-4 rounded-[14px] bg-white p-4" data-testid="design-add-pieces">
      <p className="text-[17px] font-semibold text-black">Add pieces</p>
      <div className="space-y-2">
        <Lab t="Sidelite width (in)"><input data-testid="sidelite-width-input" inputMode="decimal" value={sw} onChange={(e) => setSw(e.target.value)} className={inp} /></Lab>
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="add-sidelite-left" onClick={() => set(addSidelites(d, "left", n(sw)))} className={btn}><PanelLeft className="h-4 w-4" /> Left</button>
          <button data-testid="add-sidelite-right" onClick={() => set(addSidelites(d, "right", n(sw)))} className={btn}><PanelRight className="h-4 w-4" /> Right</button>
          <button data-testid="add-sidelite-both" onClick={() => set(addSidelites(d, "both", n(sw)))} className={btn}>Both</button>
        </div>
      </div>
      <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
        <Lab t="Transom height"><input data-testid="transom-height-input" inputMode="decimal" value={th} onChange={(e) => setTh(e.target.value)} className={inp} /></Lab>
        <Lab t="Shape"><select data-testid="transom-shape-select" value={top} onChange={(e) => setTop(e.target.value)} className={inp}>{TOPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lab>
        <button data-testid="add-transom-button" onClick={() => set(addTransom(d, n(th), top))} className={btn}><Plus className="h-4 w-4" /> Transom</button>
      </div>
      <div className="grid grid-cols-3 items-end gap-2">
        <Lab t="Stack unit"><select data-testid="stack-side-select" value={st.side} onChange={(e) => setSt({ ...st, side: e.target.value })} className={inp}>
          {[["top", "Above"], ["bottom", "Below"], ["left", "Left"], ["right", "Right"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lab>
        <Lab t="Size (in)"><input data-testid="stack-size-input" inputMode="decimal" value={st.size} onChange={(e) => setSt({ ...st, size: e.target.value })} className={inp} /></Lab>
        <Lab t="Kind"><select data-testid="stack-kind-select" value={st.kind} onChange={(e) => setSt({ ...st, kind: e.target.value })} className={inp}>
          <option value="window">Window</option><option value="door">Door</option></select></Lab>
      </div>
      <button data-testid="stack-unit-button" onClick={() => set(stackPiece(d, st.side, n(st.size), st.kind))} className={`${btn} w-full`}>
        {st.side === "bottom" ? <ArrowDownToLine className="h-4 w-4" /> : <ArrowUpToLine className="h-4 w-4" />} Stack unit
      </button>
    </div>
  );
}
